import { useState, useRef, useEffect } from "react";
import { useToolOpener } from "@/lib/dockBus";
import { useNavigate } from "react-router-dom";
import { Mic, Square, Loader2, Sparkles, Send, Volume2, VolumeX, ShoppingBag, Briefcase, GraduationCap, Store, Home, Camera, FileText, Rocket, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const MOD_ICON = { market: ShoppingBag, jobs: Briefcase, learn: GraduationCap, local: Store, properties: Home };
const EXAMPLES = ["Find me a plumber near here", "A free course to learn Excel", "I want to sell my old phone", "Somewhere to rent in Gaborone"];

export const PulaVoice = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useToolOpener("voice", () => setOpen(true));
  const [nudge, setNudge] = useState(false);
  const dismissNudge = () => { setNudge(false); try { localStorage.setItem("pula_voice_nudge_seen", "1"); } catch { /* noop */ } };
  const openFromNudge = () => { dismissNudge(); setOpen(true); };
  useEffect(() => {
    try { if (localStorage.getItem("pula_voice_nudge_seen")) return; } catch { return; }
    const t = setTimeout(() => setNudge((n) => (open ? n : true)), 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [plan, setPlan] = useState(null);
  const [text, setText] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const scanRef = useRef(null);
  const scanModeRef = useRef("identify");
  const audioRef = useRef(null);
  const navigate = useNavigate();

  const clearResults = () => { setResp(null); setScanResult(null); setPlan(null); };

  const speak = (msg, lang, audioB64) => {
    // Prefer Halle's warm, consistent voice (server TTS); fall back to browser speech.
    if (audioB64) {
      try {
        window.speechSynthesis?.cancel();
        if (audioRef.current) audioRef.current.pause();
        const a = new Audio(`data:audio/mp3;base64,${audioB64}`);
        audioRef.current = a;
        a.onplay = () => setSpeaking(true);
        a.onended = () => setSpeaking(false);
        a.play().catch(() => {});
        return;
      } catch { /* fall through */ }
    }
    try {
      if (!("speechSynthesis" in window) || !msg) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(msg);
      u.lang = ({ tn: "en-ZA", sn: "en-ZA", nd: "en-ZA", st: "en-ZA", zu: "en-ZA" })[lang] || (lang || "en");
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    } catch { /* no-op */ }
  };
  const stopSpeak = () => { try { window.speechSynthesis.cancel(); } catch {} try { audioRef.current?.pause(); } catch {} setSpeaking(false); };

  useEffect(() => { if (!open) { stopSpeak(); stopTracks(); } /* eslint-disable-next-line */ }, [open]);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-pula-voice", h);
    return () => window.removeEventListener("open-pula-voice", h);
  }, []);

  const stopTracks = () => { try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {} streamRef.current = null; };

  const send = async ({ audioBlob, q } = {}) => {
    setBusy(true); clearResults(); stopSpeak();
    try {
      const fd = new FormData();
      if (audioBlob) fd.append("audio", new File([audioBlob], "ask.webm", { type: audioBlob.type || "audio/webm" }));
      if (q) fd.append("text", q);
      fd.append("near", localStorage.getItem("pula_area") || "");
      fd.append("speak", "1");
      const { data } = await api.post("/assistant/ask", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResp(data);
      speak(data.reply, data.language, data.audio_base64);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sorry, I couldn't process that. Please try again.");
    } finally { setBusy(false); }
  };

  const fileToB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  const pickScan = (mode) => { scanModeRef.current = mode; scanRef.current?.click(); };
  const onScanFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true); clearResults(); stopSpeak();
    try {
      const image_base64 = await fileToB64(file);
      const scanLang = localStorage.getItem("pula_lang") || "en";
      const { data } = await api.post("/assistant/scan", { image_base64, mode: scanModeRef.current, language: scanLang });
      if (!data.ok) { toast.message(data.reply || "Couldn't read that photo."); }
      else { setScanResult(data); speak(data.mode === "explain" ? `${data.title}. ${data.summary}` : (data.note || data.label), scanLang); }
    } catch { toast.error("Couldn't scan that photo — try again."); }
    finally { setBusy(false); }
  };
  const runLevelUp = async () => {
    if (!user) { toast.message("Sign in to get your Level-Up plan."); return; }
    setBusy(true); clearResults(); stopSpeak();
    const lang = localStorage.getItem("pula_lang") || "en";
    try { const { data } = await api.get(`/assistant/level-up?lang=${encodeURIComponent(lang)}`); if (data.ok) { setPlan(data); speak(data.plan?.greeting, lang); } }
    catch { toast.error("Couldn't build your plan right now."); }
    finally { setBusy(false); }
  };
  const saveAlert = async (query) => {
    try { await api.post("/market/saved-searches", { q: query }); toast.success(`Saved — we'll alert you the moment "${query}" is listed on Pula Market. 🌧️`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save that alert."); }
  };

  const startRecording = async () => {
    if (typeof MediaRecorder === "undefined") { toast.error("Your browser can't record audio — please type instead."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = ["audio/webm", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopTracks();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1200) { toast.message("That was too short — hold the button while you speak."); return; }
        await send({ audioBlob: blob });
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Please allow microphone access, or type your request below.");
    }
  };

  const stopRecording = () => {
    setRecording(false);
    try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch {}
  };

  const submitText = (e) => {
    e?.preventDefault?.();
    const q = text.trim();
    if (!q) return;
    send({ q });
  };

  return (
    <>
      {/* First-visit nudge — appears once, invites talking in any language */}
      {nudge && !open && (
        <div data-testid="pula-voice-nudge" className="fixed left-4 bottom-36 md:bottom-24 z-40 w-64 rounded-2xl bg-white shadow-xl border border-[var(--pula-border)] p-3 animate-in fade-in slide-in-from-bottom-2">
          <button data-testid="nudge-dismiss" onClick={dismissNudge} aria-label="Dismiss" className="absolute top-2 right-2 text-pula-inksoft hover:text-pula-ink"><X className="w-4 h-4" /></button>
          <button data-testid="nudge-open" onClick={openFromNudge} className="flex items-start gap-2.5 text-left">
            <span className="grid place-items-center w-9 h-9 rounded-full overflow-hidden ring-1 ring-pula-green/30 shrink-0"><img src="/halle.jpg" alt="Halle" className="w-full h-full object-cover" /></span>
            <span>
              <span className="block font-heading font-bold text-sm text-pula-ink">🎙️ New here? Just talk to me</span>
              <span className="block text-xs text-pula-inksoft mt-0.5">Ask Halle anything in your own language — jobs, homes, courses & more.</span>
            </span>
          </button>
        </div>
      )}

      {/* Floating launcher */}
      <button
        data-testid="pula-voice-launcher"
        onClick={() => { dismissNudge(); setOpen(true); }}
        aria-label="Ask Halle"
        className="fixed left-4 bottom-20 md:bottom-6 z-40 flex items-center gap-2 rounded-full bg-pula-green text-white pl-3 pr-4 py-3 shadow-lg shadow-pula-green/30 hover:bg-pula-greenhover transition-transform hover:scale-105 active:scale-95"
      >
        <span className="grid place-items-center w-7 h-7 rounded-full overflow-hidden ring-1 ring-white/50"><img src="/halle.jpg" alt="Halle" className="w-full h-full object-cover" /></span>
        <span className="font-heading font-bold text-sm">Ask Halle</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pula-voice-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <img src="/halle.jpg" alt="Halle" className="w-6 h-6 rounded-full object-cover" /> Ask Halle
            </DialogTitle>
            <DialogDescription>Hi, I'm Halle 👋 Speak or type in any language — I'll help you find what you need across Pula.</DialogDescription>
          </DialogHeader>

          {/* Mic */}
          <div className="flex flex-col items-center py-2">
            <button
              data-testid="pula-voice-mic"
              onClick={recording ? stopRecording : startRecording}
              disabled={busy}
              className={`relative grid place-items-center w-20 h-20 rounded-full text-white transition-colors ${recording ? "bg-pula-accent" : "bg-pula-green hover:bg-pula-greenhover"} disabled:opacity-60`}
            >
              {recording && <span className="absolute inset-0 rounded-full bg-pula-accent/40 animate-ping" />}
              {busy ? <Loader2 className="w-8 h-8 animate-spin" /> : recording ? <Square className="w-7 h-7" /> : <Mic className="w-8 h-8" />}
            </button>
            <p className="text-xs text-pula-inksoft font-body mt-2">{recording ? "Listening… tap to stop" : busy ? "Thinking…" : "Tap and speak"}</p>
          </div>

          {/* Type fallback */}
          <form onSubmit={submitText} className="flex items-center gap-2">
            <Input data-testid="pula-voice-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="…or type your request" />
            <Button type="submit" data-testid="pula-voice-send" disabled={busy || !text.trim()} className="rounded-full bg-pula-green hover:bg-pula-greenhover text-white shrink-0"><Send className="w-4 h-4" /></Button>
          </form>

          {/* Camera + plan actions */}
          <div className="flex flex-wrap gap-2">
            <button data-testid="pula-voice-scan-identify" onClick={() => pickScan("identify")} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pula-bg2 text-pula-ink text-xs font-semibold hover:bg-pula-green/10 hover:text-pula-green disabled:opacity-50"><Camera className="w-3.5 h-3.5" /> What's this?</button>
            <button data-testid="pula-voice-scan-explain" onClick={() => pickScan("explain")} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pula-bg2 text-pula-ink text-xs font-semibold hover:bg-pula-green/10 hover:text-pula-green disabled:opacity-50"><FileText className="w-3.5 h-3.5" /> Explain a document</button>
            {user && (
              <button data-testid="pula-voice-levelup" onClick={runLevelUp} disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pula-ink text-white text-xs font-semibold hover:bg-pula-ink/90 disabled:opacity-50"><Rocket className="w-3.5 h-3.5" /> My Level-Up plan</button>
            )}
          </div>
          <input ref={scanRef} type="file" accept="image/*" capture="environment" hidden onChange={onScanFile} data-testid="pula-voice-scan-input" />

          {!resp && !scanResult && !plan && !busy && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {EXAMPLES.map((ex) => (
                <button key={ex} data-testid={`pula-voice-example`} onClick={() => { setText(ex); send({ q: ex }); }}
                  className="px-3 py-1 rounded-full bg-pula-bg2 text-pula-inksoft text-xs font-medium hover:bg-pula-green/10 hover:text-pula-green">{ex}</button>
              ))}
            </div>
          )}

          {/* Response */}
          {resp && (
            <div className="mt-2 space-y-3" data-testid="pula-voice-response">
              {resp.transcript && <p className="text-xs text-pula-inksoft font-body italic">You said: “{resp.transcript}”</p>}
              <div className="flex items-start gap-2 p-3 rounded-xl bg-pula-green/8 border border-pula-green/20">
                <Sparkles className="w-4 h-4 text-pula-green mt-0.5 shrink-0" />
                <p className="text-sm font-body text-pula-ink flex-1" data-testid="pula-voice-reply">{resp.reply}</p>
                <button onClick={() => (speaking ? stopSpeak() : speak(resp.reply, resp.language, resp.audio_base64))} className="text-pula-green shrink-0" aria-label="Read aloud" data-testid="pula-voice-speak">
                  {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>

              {(resp.groups || []).map((g) => {
                const Icon = MOD_ICON[g.module] || ShoppingBag;
                return (
                  <div key={g.module}>
                    <p className="text-xs font-heading font-bold text-pula-ink flex items-center gap-1.5 mb-1.5"><Icon className="w-3.5 h-3.5 text-pula-green" /> {g.label}</p>
                    <div className="space-y-1.5">
                      {g.items.map((it, i) => (
                        <button key={i} data-testid="pula-voice-result"
                          onClick={() => { setOpen(false); navigate(it.link); }}
                          className="w-full text-left flex items-center gap-3 p-2 rounded-lg border border-[var(--pula-border)] hover:border-pula-green transition-colors">
                          {it.image ? <img src={it.image.startsWith("http") ? it.image : `${API}/files/${it.image}`} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                            : <span className="w-10 h-10 rounded bg-pula-bg2 grid place-items-center shrink-0"><Icon className="w-4 h-4 text-pula-inksoft" /></span>}
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-pula-ink line-clamp-1">{it.title}</span>
                            {it.subtitle && <span className="block text-xs text-pula-inksoft line-clamp-1">{it.subtitle}</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              {resp.total === 0 && (
                <div className="text-center py-2 space-y-2" data-testid="pula-voice-zero">
                  <p className="text-sm text-pula-inksoft font-body">Nothing listed for that yet — try different words.</p>
                  {user && resp.query && (
                    <Button data-testid="pula-voice-save-alert" onClick={() => saveAlert(resp.query)} variant="outline" className="rounded-full text-pula-green border-pula-green/40">
                      <Sparkles className="w-4 h-4 mr-1.5" /> Alert me when "{resp.query}" is listed
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Scan result */}
          {scanResult && (
            <div className="mt-2 space-y-3" data-testid="pula-voice-scan-result">
              {scanResult.mode === "explain" ? (
                <div className="p-3 rounded-xl bg-pula-green/8 border border-pula-green/20">
                  <p className="font-heading font-bold text-pula-ink flex items-center gap-1.5"><FileText className="w-4 h-4 text-pula-green" /> {scanResult.title}</p>
                  <p className="text-sm font-body text-pula-ink mt-1">{scanResult.summary}</p>
                  {(scanResult.points || []).length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {scanResult.points.map((p, i) => (
                        <li key={i} className="text-sm text-pula-ink font-body flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-pula-green mt-0.5 shrink-0" /> {p}</li>
                      ))}
                    </ul>
                  )}
                  {scanResult.action && <p className="text-sm font-semibold text-pula-accent mt-2">👉 {scanResult.action}</p>}
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-pula-green/8 border border-pula-green/20">
                    <Camera className="w-4 h-4 text-pula-green mt-0.5 shrink-0" />
                    <p className="text-sm font-body text-pula-ink flex-1"><b>{scanResult.label}</b>{scanResult.note ? ` — ${scanResult.note}` : ""}</p>
                  </div>
                  {(scanResult.groups || []).map((g) => {
                    const Icon = MOD_ICON[g.module] || ShoppingBag;
                    return (
                      <div key={g.module}>
                        <p className="text-xs font-heading font-bold text-pula-ink flex items-center gap-1.5 mb-1.5"><Icon className="w-3.5 h-3.5 text-pula-green" /> {g.label}</p>
                        <div className="space-y-1.5">
                          {g.items.map((it, i) => (
                            <button key={i} data-testid="pula-voice-result" onClick={() => { setOpen(false); navigate(it.link); }}
                              className="w-full text-left flex items-center gap-3 p-2 rounded-lg border border-[var(--pula-border)] hover:border-pula-green transition-colors">
                              {it.image ? <img src={it.image.startsWith("http") ? it.image : `${API}/files/${it.image}`} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                                : <span className="w-10 h-10 rounded bg-pula-bg2 grid place-items-center shrink-0"><Icon className="w-4 h-4 text-pula-inksoft" /></span>}
                              <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-pula-ink line-clamp-1">{it.title}</span>{it.subtitle && <span className="block text-xs text-pula-inksoft line-clamp-1">{it.subtitle}</span>}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {scanResult.total === 0 && <p className="text-sm text-pula-inksoft font-body text-center">No similar items listed yet — want to sell yours? Tap "List an item" in Market.</p>}
                </>
              )}
            </div>
          )}

          {/* Level-Up plan */}
          {plan && (
            <div className="mt-2 space-y-3" data-testid="pula-voice-plan">
              <div className="p-3 rounded-xl bg-pula-ink text-white">
                <p className="font-heading font-bold flex items-center gap-1.5"><Rocket className="w-4 h-4" /> {plan.plan?.greeting}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs opacity-90">
                  <span>{plan.stats?.completed} course(s) done</span>
                  <span>{plan.stats?.skills} skill(s)</span>
                  <span>{plan.stats?.matching_jobs} matching job(s)</span>
                </div>
              </div>
              {(plan.plan?.wins || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {plan.plan.wins.map((w, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-pula-green/10 text-pula-green font-semibold">🎉 {w}</span>)}
                </div>
              )}
              <div className="space-y-1.5">
                {(plan.plan?.steps || []).map((s, i) => (
                  <div key={i} className="pula-card p-3">
                    <p className="text-sm font-heading font-bold text-pula-ink">{i + 1}. {s.title}</p>
                    {s.why && <p className="text-xs text-pula-inksoft font-body mt-0.5">{s.why}</p>}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button data-testid="plan-goto-learn" onClick={() => { setOpen(false); navigate(plan.links?.learn || "/learn"); }} className="rounded-full bg-pula-green text-white flex-1"><GraduationCap className="w-4 h-4 mr-1" /> Free courses</Button>
                <Button data-testid="plan-goto-jobs" variant="outline" onClick={() => { setOpen(false); navigate(plan.links?.jobs || "/jobs"); }} className="rounded-full flex-1"><Briefcase className="w-4 h-4 mr-1" /> Jobs</Button>
              </div>
              {plan.plan?.encourage && <p className="text-sm font-body text-pula-green text-center italic">{plan.plan.encourage}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
